import * as React from 'react';
import type {ReactNode} from 'react';
import {cn} from '../../lib/cn';
import type {OptionProps} from '../_select-internals';
import type {SelectValue} from '../_select-internals';
import {VisualCheckbox} from './VisualCheckbox';

function SelectCheckboxOption<T, V extends SelectValue, L extends ReactNode>(
  props: OptionProps<T, V, L>,
) {
  const {
    option,
    index,
    isSelected,
    isHighlighted,
    isDisabled,
    id,
    onClick,
    onHover,
    innerRef,
  } = props;

  return (
    <button
      id={id}
      ref={innerRef}
      type="button"
      role="option"
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      tabIndex={-1}
      disabled={isDisabled}
      onClick={() => onClick(option)}
      onMouseMove={() => onHover(index)}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border-0 bg-transparent px-3 py-2 text-left text-[15px] font-medium leading-5 text-[var(--chekin-color-brand-navy)] outline-none transition-colors duration-100',
        !isDisabled && 'cursor-pointer hover:bg-[var(--chekin-color-surface-pressed)]',
        isHighlighted && !isDisabled && 'bg-[var(--chekin-color-surface-pressed)]',
        isSelected &&
          'bg-[var(--chekin-color-surface-autocomplete)] font-semibold text-[var(--chekin-color-brand-blue)]',
        isDisabled && 'cursor-not-allowed text-[var(--chekin-color-gray-2)]',
      )}
    >
      <VisualCheckbox checked={isSelected} disabled={isDisabled} />
      <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <span className="block break-words">{option.label as ReactNode}</span>
        {option.description && (
          <span className="shrink-0 text-[12px] font-medium italic text-[var(--chekin-color-gray-1)]">
            {option.description}
          </span>
        )}
      </span>
    </button>
  );
}

export {SelectCheckboxOption};
